import apiClient from "./axios";
import { clearStoredTokens } from "@/store/authStore";
import type { Department } from "@/types";

// ============================================================================
// SHARED TYPES & UTILITIES
// ============================================================================

export interface PaginationQuery {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  search?: string;
  [key: string]: unknown;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

function buildParams(query?: PaginationQuery): string {
  if (!query) return "";
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });
  const str = params.toString();
  return str ? `?${str}` : "";
}

// ============================================================================
// AUTH API
// ============================================================================

export const authApi = {
  register: async (data: { email: string; password: string; name: string }) => {
    const response = await apiClient.post("/auth/register", data);
    return response.data;
  },

  login: async (data: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }) => {
    const response = await apiClient.post("/auth/login", data);
    return response.data;
  },

  /** Valida el token de invitación (público) → { email, name }. */
  validateInvite: async (
    token: string,
  ): Promise<{ email: string; name: string }> => {
    const response = await apiClient.get(`/auth/invite/${token}`);
    return response.data;
  },

  /** Fija la contraseña desde una invitación (público). */
  setPassword: async (token: string, password: string) => {
    const response = await apiClient.post("/auth/set-password", {
      token,
      password,
    });
    return response.data;
  },

  googleLogin: async (idToken: string, rememberMe?: boolean) => {
    const response = await apiClient.post("/auth/google", {
      idToken,
      rememberMe,
    });
    return response.data;
  },

  /** Revoca la sesión server-side (cookie httpOnly) y limpia el cliente. */
  logout: async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch {
      // Best-effort: sin red, la cookie expira sola y el access token muere en 15m.
    }
    clearStoredTokens();
  },

  /** Vuelve a la cuenta original desde una impersonación (usa la cookie
   *  httpOnly de refresh; el cliente no guarda ningún backup de tokens). */
  stopImpersonation: async () => {
    const response = await apiClient.post("/auth/impersonate/stop");
    return response.data;
  },

  getProfile: async () => {
    const response = await apiClient.get("/auth/me");
    return response.data;
  },

  impersonate: async (userId: string) => {
    const response = await apiClient.post(`/auth/impersonate/${userId}`);
    return response.data;
  },
};

// ============================================================================
// USERS API
// ============================================================================

export const usersApi = {
  getAll: async (query?: PaginationQuery) => {
    const response = await apiClient.get(`/users${buildParams(query)}`);
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  },

  create: async (data: {
    email: string;
    /** Opcional: si se omite, el usuario se crea sin contraseña y recibe un
     *  email de invitación para fijarla él mismo. */
    password?: string;
    name: string;
    roleIds?: string[];
    /** Departamentos a los que pertenece. */
    departmentIds?: string[];
    brandIds?: string[];
    locationIds?: string[];
    isActive?: boolean;
    /** Id numérico de Telegram: enlaza sus notas de prensa. */
    telegramUserId?: string | null;
  }) => {
    const response = await apiClient.post("/users", data);
    return response.data;
  },

  /** Reenvía el email de invitación (nuevo enlace para fijar contraseña). */
  resendInvite: async (id: string) => {
    const response = await apiClient.post(`/users/${id}/resend-invite`);
    return response.data;
  },

  update: async (
    id: string,
    data: {
      name?: string;
      email?: string;
      roleIds?: string[];
      /** Departamentos a los que pertenece (sustituye la lista). */
      departmentIds?: string[];
      brandIds?: string[];
      locationIds?: string[];
      password?: string;
      isActive?: boolean;
      /** Mover de empresa (solo super-admin de plataforma). */
      tenantId?: string;
      /** Id numérico de Telegram; '' o null desvincula. */
      telegramUserId?: string | null;
    },
  ) => {
    const response = await apiClient.patch(`/users/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/users/${id}`);
    return response.data;
  },

  hardDelete: async (id: string) => {
    const response = await apiClient.delete(`/users/${id}/permanent`);
    return response.data;
  },

  restore: async (id: string) => {
    const response = await apiClient.patch(`/users/${id}/restore`);
    return response.data;
  },

  uploadAvatar: async (file: File) => {
    const formData = new FormData();
    formData.append("avatar", file);
    const response = await apiClient.patch("/users/me/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiClient.patch("/users/me/password", {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

// ============================================================================
// ROLES & PERMISSIONS API
// ============================================================================

export interface PermissionFlags {
  canView: boolean;
  /** Visibilidad en el sidebar/menú lateral, independiente de `canView`.
   *  `canView` permite leer/usar los datos y abrir la tabla; este flag solo
   *  decide si la feature aparece como entrada de menú. */
  canViewSidebar?: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove?: boolean;
  canIssue?: boolean;
  canExecute?: boolean;
}

export interface FeatureMeta {
  key: string;
  label: string;
  section?: string;
  actions: (keyof PermissionFlags)[];
}

export type PermissionsMap = Record<string, PermissionFlags>;

// ─── Departamentos ─────────────────────────────────────────────────────
// ─── Generador de informes de clipping (Editorial) ─────────────────────────
export type ClippingReportFormat = "PDF" | "XLSX" | "PPTX" | "DOCX";

export interface ClippingClient {
  id: string;
  name: string;
  brand: string;
  slug: string;
  aliases: string[];
  language: string;
  timezone: string;
  logoPath: string | null;
  defaultTemplateId: string | null;
  defaultRecipients: string[];
  aiExamples: Array<{ period: string; text: string }> | null;
  notes: string | null;
  isActive: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  defaultTemplate: { id: string; name: string } | null;
  _count: { templates: number; configs: number; events: number };
  lastRunAt?: string | null;
}

export interface ClippingClientOptions {
  brands: string[];
  topics: Array<{ value: string; count: number }>;
  categories: Array<{ value: string; count: number }>;
  sections: Array<{ value: string; count: number }>;
  outlets: Array<{ value: string; count: number }>;
  countries: Array<{ value: string; count: number }>;
  regions: Array<{ value: string; count: number }>;
  years: number[];
}

export interface ClippingEvent {
  id: string;
  clientId: string;
  name: string;
  description: string | null;
  keywords: string[];
  excludeKeywords: string[];
  startsOn: string | null;
  endsOn: string | null;
  filters: Record<string, unknown> | null;
  isActive: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  client: { id: string; name: string; brand: string };
  _count: { configs: number; runs: number };
}

export interface ClippingEventMatches {
  from: string;
  to: string;
  count: number;
  sample: Array<{ id: string; title: string; outlet: string; publishedAt: string; mediaType: ClippingMediaType }>;
}

/** Definición de plantilla (espejo de template-definition.ts del backend, que la valida). */
export type ClippingReportDataset =
  | "byMediaType"
  | "byTopic"
  | "byOutlet"
  | "byDay"
  | "byMonth"
  | "bySentiment"
  | "byAuthor"
  | "byCategory"
  | "bySection"
  | "byRegion"
  | "byCountry"
  | "byWeekday"
  | "byBrand"
  | "byEvent"
  | "sentimentByMediaType";
export type ClippingReportChartKind = "bar" | "barh" | "line" | "pie" | "donut" | "stacked";
export type ClippingReportKpiKey =
  | "count"
  | "outlets"
  | "audience"
  | "adValue"
  | "commValue"
  | "withAudience"
  | "avgPerDay"
  | "peakDay"
  | "topOutlet"
  | "positiveShare"
  | "negativeShare"
  | "printCount"
  | "digitalCount"
  | "tvCount"
  | "radioCount";
export type ClippingReportListingGroupBy = "mediaType" | "month" | "topic" | "outlet" | "category" | "event" | "none";
/** Evento del cliente tal como lo usa la vista previa (corte por evento y listado agrupado). */
export interface ClippingReportEventRef {
  id: string;
  name: string;
  keywords: string[];
  excludeKeywords: string[];
  startsOn?: string | null;
  endsOn?: string | null;
}
export type ClippingReportSortField = "publishedAt" | "outlet" | "title" | "mediaType" | "audience" | "adValue" | "topic" | "sentiment" | "section";

export interface ClippingReportSectionBase {
  id: string;
  enabled: boolean;
  title?: string | null;
  formats?: ClippingReportFormat[];
  pageBreakBefore?: boolean;
}
export type ClippingReportSection =
  | (ClippingReportSectionBase & { type: "cover"; subtitle?: string | null })
  | (ClippingReportSectionBase & { type: "kpis"; metrics: ClippingReportKpiKey[]; commValueRow: boolean; compareWithPrevious: boolean })
  | (ClippingReportSectionBase & { type: "table"; dataset: ClippingReportDataset; columns: string[]; totalsRow: boolean; byMediaType?: boolean; top?: number })
  | (ClippingReportSectionBase & { type: "chart"; dataset: ClippingReportDataset; chart: ClippingReportChartKind; metric?: ClippingReportMetric; top?: number })
  | (ClippingReportSectionBase & { type: "comparison"; metrics: ClippingReportMetric[]; breakdown?: "none" | "mediaType" })
  | (ClippingReportSectionBase & { type: "ai_text"; kind: "assessment" | "executive_summary" | "custom"; instructions?: string | null; examplesSource: "client" | "none"; maxTokens: number; onError: "note" | "omit" | "fail" })
  | (ClippingReportSectionBase & { type: "text"; body: string })
  | (ClippingReportSectionBase & { type: "listing"; groupBy: ClippingReportListingGroupBy; columns: string[]; sort: { field: ClippingReportSortField; dir: "asc" | "desc" }; maxItems: Partial<Record<ClippingReportFormat, number>>; linkTitles: boolean; showSectionTitles: boolean })
  | (ClippingReportSectionBase & { type: "page_break" })
  | (ClippingReportSectionBase & { type: "highlights"; metric: "audience" | "adValue" | "relevance"; top: number; showExcerpt: boolean; showThumbnail: boolean; showMetrics: boolean })
  | (ClippingReportSectionBase & { type: "gallery"; top: number; columns: 2 | 3 | 4; sort: "publishedAt" | "audience" | "adValue"; caption: boolean })
  | (ClippingReportSectionBase & { type: "toc"; numbered: boolean })
  | (ClippingReportSectionBase & { type: "share_of_voice"; brands: string[]; chart: ClippingReportChartKind; metric?: ClippingReportMetric; showTable: boolean; top: number });
export type ClippingReportSectionType = ClippingReportSection["type"];

export interface ClippingReportTemplateStyle {
  slug: string;
  primary: string;
  onPrimary: string;
  tint: string;
  chart: string;
  cover: string;
  onCover: string;
  chartPalette: string[];
  font: "DejaVuSans" | "Helvetica";
  claim?: string | null;
  pageLogo: "footer" | "header-right" | "none";
  agencyLogos: boolean;
  xlsx: { header: string; onHeader: string; tint: string };
  assets: { logo: string | null; logoCover: string | null; logoWhite: string | null; cover: string | null };
}

export interface ClippingReportTemplateDefinition {
  version: 1;
  formats: ClippingReportFormat[];
  locale: "es" | "en";
  style: ClippingReportTemplateStyle;
  pdf: { layout: "band" | "mono" | "landscape" | "deck" | "atlas"; metric: ClippingReportMetric };
  xlsx: { layout: "sheets" | "blocks" | "monthly" | "yearly" | "europe" | "summary-blocks" | "atlas" };
  pptx: { layout: "deck"; source: "code" | "file" };
  docx: { layout: "report"; source: "code" | "file" };
  sections: ClippingReportSection[];
  defaults: { filters: Record<string, unknown>; metric: ClippingReportMetric; period: Record<string, unknown> & { type: string } };
  email: { subject: string; body: string };
}

export interface ClippingReportTemplateFile {
  id: string;
  format: ClippingReportFormat;
  originalName: string;
  size: number;
  placeholders: { scalars: string[]; loops: string[]; images: string[] };
  validation: { ok: boolean; errors: string[]; warnings: string[]; checkedAt: string };
  createdAt: string;
  updatedAt: string;
}

export interface ClippingReportTemplate {
  id: string;
  clientId: string | null;
  name: string;
  description: string | null;
  definition: ClippingReportTemplateDefinition;
  version: number;
  seedKey: string | null;
  isActive: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  client: { id: string; name: string; brand: string; defaultTemplateId: string | null } | null;
  files: ClippingReportTemplateFile[];
  _count: { configs: number; runs: number; defaultFor: number };
}

export interface ClippingReportTemplateCatalog {
  formats: ClippingReportFormat[];
  layouts: Record<ClippingReportFormat, Array<{ id: string; label: string }>>;
  datasets: Array<{ id: string; label: string; columns: string[] }>;
  /** Cortes con series (admiten la gráfica apilada). */
  stackedDatasets: string[];
  charts: string[];
  metrics: string[];
  kpis: string[];
  listingColumns: Array<{ key: string; label: { es: string; en: string }; type: string; width: number; align: string }>;
  maxItems: { defaults: Record<ClippingReportFormat, number>; hard: Record<ClippingReportFormat, number> };
  sectionTypes: Array<{ type: string; label: string; defaults: Record<string, unknown> }>;
  /** Secciones preparadas: un tipo con título y contenido ya escritos. */
  presets: Array<{ id: string; type: string; label: string; defaults: Record<string, unknown> }>;
  aiKinds: Array<{ id: string; label: string }>;
  placeholders: Array<{ key: string; label: string; kind: "scalar" | "loop" | "image"; fields?: string[] }>;
  notes: Record<string, string>;
}


/** Generador de informes: configuraciones y ejecuciones (fase F3). */
export type ClippingReportRunStatus = "QUEUED" | "RUNNING" | "SUCCEEDED" | "FAILED" | "CANCELLED";
export type ClippingReportTrigger = "MANUAL" | "SCHEDULED" | "RETRY" | "PREVIEW";
export type ClippingReportPeriodSpec =
  | { type: "DAILY"; offsetDays?: number }
  | { type: "WEEKLY"; offsetWeeks?: number }
  | { type: "MONTHLY"; offsetMonths?: number; month?: string }
  | { type: "CUSTOM"; from: string; to: string }
  | { type: "EVENT" };
export interface ClippingReportSchedule {
  enabled: boolean;
  frequency: "DAILY" | "WEEKLY" | "MONTHLY";
  time: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  timezone?: string;
}
export interface ClippingReportConfig {
  id: string;
  clientId: string;
  name: string;
  description: string | null;
  templateId: string;
  eventId: string | null;
  period: ClippingReportPeriodSpec;
  filters: Record<string, unknown>;
  columns: string[];
  sort: { field: ClippingReportSortField; dir: "asc" | "desc" } | null;
  formats: ClippingReportFormat[];
  metric: ClippingReportMetric;
  sendEmail: boolean;
  recipients: string[];
  subject: string | null;
  body: string | null;
  schedule: ClippingReportSchedule | null;
  scheduledTaskId: string | null;
  isActive: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  client: { id: string; name: string; brand: string; timezone: string };
  template: { id: string; name: string; version: number; isActive: boolean; deletedAt: string | null };
  event: { id: string; name: string; startsOn: string | null; endsOn: string | null } | null;
  scheduledTask: { id: string; isActive: boolean; nextExecution: string | null; lastExecution: string | null } | null;
  runs: Array<{ id: string; status: ClippingReportRunStatus; trigger: ClippingReportTrigger; from: string; to: string; createdAt: string; finishedAt: string | null; error: string | null }>;
}
export interface ClippingReportConfigInput {
  clientId: string;
  name: string;
  description?: string | null;
  templateId: string;
  eventId?: string | null;
  period: ClippingReportPeriodSpec;
  filters?: Record<string, unknown>;
  columns?: string[];
  sort?: { field: ClippingReportSortField; dir: "asc" | "desc" } | null;
  formats: ClippingReportFormat[];
  metric?: ClippingReportMetric;
  sendEmail?: boolean;
  recipients?: string[];
  subject?: string | null;
  body?: string | null;
  schedule?: ClippingReportSchedule | null;
  isActive?: boolean;
}
export interface ClippingReportResolved {
  period: { from: string; to: string; label: string };
  brands: string[];
  filters: Record<string, unknown>;
  columns: string[];
  formats: ClippingReportFormat[];
  metric: ClippingReportMetric;
  template: { id: string; name: string; version: number };
  event: { id: string; name: string; keywords: string[] } | null;
  warnings: string[];
  count?: number;
}
export interface ClippingReportRunFile {
  id: string;
  format: ClippingReportFormat;
  fileName: string;
  mimeType: string;
  size: number;
  createdAt: string;
}
export interface ClippingReportDelivery {
  id: string;
  recipient: string;
  mode: "ATTACHMENT" | "LINK";
  subject: string;
  status: "PENDING" | "SENT" | "FAILED" | "SKIPPED";
  attempt: number;
  error: string | null;
  sentAt: string | null;
  requestedById: string | null;
  createdAt: string;
}
export interface ClippingReportDeliverySummary {
  sent: number;
  failed: number;
  skipped: number;
  mode: "ATTACHMENT" | "LINK" | null;
}
export interface ClippingReportRunCounts {
  items: number;
  itemsTotal: number;
  truncated: boolean;
  queryMs: number;
  aiMs: number;
  renderMs: number;
  bytes: number;
  failedFormats: ClippingReportFormat[];
  errors: Record<string, string>;
  emailSent?: number;
  emailFailed?: number;
}
export interface ClippingReportRun {
  id: string;
  clientId: string;
  configId: string | null;
  templateId: string | null;
  templateVersion: number | null;
  eventId: string | null;
  trigger: ClippingReportTrigger;
  status: ClippingReportRunStatus;
  from: string;
  to: string;
  progress: number;
  stage: string | null;
  counts: ClippingReportRunCounts | null;
  error: string | null;
  attempt: number;
  retryOfId: string | null;
  requestedById: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  expiresAt: string | null;
  filesPurgedAt: string | null;
  createdAt: string;
  updatedAt: string;
  client: { id: string; name: string; brand: string; slug: string };
  config: { id: string; name: string } | null;
  template: { id: string; name: string } | null;
  event: { id: string; name: string } | null;
  files: ClippingReportRunFile[];
  deliveries: ClippingReportDelivery[];
  snapshot?: {
    brands: string[];
    filters: Record<string, unknown>;
    columns: string[];
    sort: { field: string; dir: string } | null;
    formats: ClippingReportFormat[];
    metric: ClippingReportMetric;
    templateName: string;
    sendEmail: boolean;
    recipients: string[];
    subject: string | null;
    body: string | null;
    periodLabel: string;
    event: { id: string; name: string; keywords: string[] } | null;
    configName: string | null;
  };
}
export interface ClippingReportRunsPage {
  data: ClippingReportRun[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}
export interface ClippingReportStats {
  runs: Partial<Record<ClippingReportRunStatus, number>>;
  queued: number;
  failedDeliveries: number;
  storage: { bytes: number; folders: number; maxBytes: number; retentionDays: number };
}

export const clippingReportsApi = {
  clients: {
    list: async (opts: { search?: string; inactive?: boolean } = {}): Promise<ClippingClient[]> => {
      const response = await apiClient.get("/clipping-reports/clients", {
        params: { search: opts.search || undefined, inactive: opts.inactive ? 1 : undefined },
      });
      return response.data;
    },
    get: async (id: string): Promise<ClippingClient & { templates: Array<{ id: string; name: string; version: number; isActive: boolean; seedKey: string | null; updatedAt: string }>; events: Array<{ id: string; name: string; startsOn: string | null; endsOn: string | null; isActive: boolean }>; configs: Array<{ id: string; name: string; formats: ClippingReportFormat[]; isActive: boolean; schedule: Record<string, unknown> | null }> }> => {
      const response = await apiClient.get(`/clipping-reports/clients/${id}`);
      return response.data;
    },
    options: async (id: string): Promise<ClippingClientOptions> => {
      const response = await apiClient.get(`/clipping-reports/clients/${id}/options`);
      return response.data;
    },
    unassignedBrands: async (): Promise<Array<{ brand: string; count: number }>> => {
      const response = await apiClient.get("/clipping-reports/clients/brands/unassigned");
      return response.data;
    },
    create: async (data: { name: string; brand: string; aliases?: string[]; language?: string; timezone?: string; defaultRecipients?: string[]; notes?: string | null }): Promise<ClippingClient> => {
      const response = await apiClient.post("/clipping-reports/clients", data);
      return response.data;
    },
    update: async (id: string, data: Partial<{ name: string; brand: string; aliases: string[]; language: string; timezone: string; defaultRecipients: string[]; notes: string | null; defaultTemplateId: string | null; aiExamples: Array<{ period: string; text: string }> | null; isActive: boolean }>): Promise<ClippingClient> => {
      const response = await apiClient.patch(`/clipping-reports/clients/${id}`, data);
      return response.data;
    },
    remove: async (id: string) => {
      const response = await apiClient.delete(`/clipping-reports/clients/${id}`);
      return response.data;
    },
    restore: async (id: string): Promise<ClippingClient> => {
      const response = await apiClient.post(`/clipping-reports/clients/${id}/restore`);
      return response.data;
    },
  },
  events: {
    list: async (opts: { clientId?: string; search?: string; inactive?: boolean } = {}): Promise<ClippingEvent[]> => {
      const response = await apiClient.get("/clipping-reports/events", {
        params: { clientId: opts.clientId || undefined, search: opts.search || undefined, inactive: opts.inactive ? 1 : undefined },
      });
      return response.data;
    },
    matches: async (id: string, opts: { from?: string; to?: string } = {}): Promise<ClippingEventMatches> => {
      const response = await apiClient.get(`/clipping-reports/events/${id}/matches`, { params: opts });
      return response.data;
    },
    create: async (data: { clientId: string; name: string; description?: string | null; keywords: string[]; excludeKeywords?: string[]; startsOn?: string | null; endsOn?: string | null; filters?: Record<string, unknown> | null }): Promise<ClippingEvent> => {
      const response = await apiClient.post("/clipping-reports/events", data);
      return response.data;
    },
    update: async (id: string, data: Partial<{ clientId: string; name: string; description: string | null; keywords: string[]; excludeKeywords: string[]; startsOn: string | null; endsOn: string | null; filters: Record<string, unknown> | null; isActive: boolean }>): Promise<ClippingEvent> => {
      const response = await apiClient.patch(`/clipping-reports/events/${id}`, data);
      return response.data;
    },
    remove: async (id: string) => {
      const response = await apiClient.delete(`/clipping-reports/events/${id}`);
      return response.data;
    },
    restore: async (id: string): Promise<ClippingEvent> => {
      const response = await apiClient.post(`/clipping-reports/events/${id}/restore`);
      return response.data;
    },
  },
  configs: {
    list: async (opts: { clientId?: string; inactive?: boolean } = {}): Promise<ClippingReportConfig[]> => {
      const response = await apiClient.get("/clipping-reports/configs", {
        params: { clientId: opts.clientId || undefined, inactive: opts.inactive ? 1 : undefined },
      });
      return response.data;
    },
    get: async (id: string): Promise<ClippingReportConfig> => {
      const response = await apiClient.get(`/clipping-reports/configs/${id}`);
      return response.data;
    },
    resolve: async (id: string, date?: string): Promise<ClippingReportResolved> => {
      const response = await apiClient.get(`/clipping-reports/configs/${id}/resolve`, { params: { date: date || undefined } });
      return response.data;
    },
    create: async (data: ClippingReportConfigInput): Promise<ClippingReportConfig> => {
      const response = await apiClient.post("/clipping-reports/configs", data);
      return response.data;
    },
    update: async (id: string, data: Partial<ClippingReportConfigInput>): Promise<ClippingReportConfig> => {
      const response = await apiClient.patch(`/clipping-reports/configs/${id}`, data);
      return response.data;
    },
    duplicate: async (id: string, data: { name?: string } = {}): Promise<ClippingReportConfig> => {
      const response = await apiClient.post(`/clipping-reports/configs/${id}/duplicate`, data);
      return response.data;
    },
    remove: async (id: string) => {
      const response = await apiClient.delete(`/clipping-reports/configs/${id}`);
      return response.data;
    },
    restore: async (id: string): Promise<ClippingReportConfig> => {
      const response = await apiClient.post(`/clipping-reports/configs/${id}/restore`);
      return response.data;
    },
    run: async (id: string, data: { from?: string; to?: string; formats?: ClippingReportFormat[]; sendEmail?: boolean } = {}): Promise<ClippingReportRun> => {
      const response = await apiClient.post(`/clipping-reports/configs/${id}/run`, data);
      return response.data;
    },
  },
  runs: {
    list: async (opts: { clientId?: string; configId?: string; status?: ClippingReportRunStatus; trigger?: ClippingReportTrigger; page?: number; limit?: number } = {}): Promise<ClippingReportRunsPage> => {
      const response = await apiClient.get("/clipping-reports/runs", {
        params: { ...opts, clientId: opts.clientId || undefined, configId: opts.configId || undefined, status: opts.status || undefined, trigger: opts.trigger || undefined },
      });
      return response.data;
    },
    stats: async (): Promise<ClippingReportStats> => {
      const response = await apiClient.get("/clipping-reports/runs/stats");
      return response.data;
    },
    get: async (id: string): Promise<ClippingReportRun> => {
      const response = await apiClient.get(`/clipping-reports/runs/${id}`);
      return response.data;
    },
    create: async (data: { clientId: string; templateId?: string | null; eventId?: string | null; from: string; to: string; filters?: Record<string, unknown>; columns?: string[]; sort?: Record<string, unknown> | null; formats: ClippingReportFormat[]; metric?: ClippingReportMetric; sendEmail?: boolean; recipients?: string[]; subject?: string | null; body?: string | null }): Promise<ClippingReportRun> => {
      const response = await apiClient.post("/clipping-reports/runs", data);
      return response.data;
    },
    cancel: async (id: string): Promise<ClippingReportRun> => {
      const response = await apiClient.post(`/clipping-reports/runs/${id}/cancel`);
      return response.data;
    },
    retry: async (id: string): Promise<ClippingReportRun> => {
      const response = await apiClient.post(`/clipping-reports/runs/${id}/retry`);
      return response.data;
    },
    remove: async (id: string) => {
      const response = await apiClient.delete(`/clipping-reports/runs/${id}`);
      return response.data;
    },
    /** Descarga con sesión: devuelve el blob. */
    download: async (runId: string, fileId: string): Promise<Blob> => {
      const response = await apiClient.get(`/clipping-reports/runs/${runId}/files/${fileId}`, { responseType: "blob" });
      return response.data;
    },
    link: async (runId: string, fileId: string): Promise<{ url: string | null; expiresAt: string | null }> => {
      const response = await apiClient.get(`/clipping-reports/runs/${runId}/files/${fileId}/link`);
      return response.data;
    },
    send: async (runId: string, data: { recipients?: string[]; subject?: string | null; body?: string | null } = {}): Promise<ClippingReportDeliverySummary> => {
      const response = await apiClient.post(`/clipping-reports/runs/${runId}/send`, data);
      return response.data;
    },
  },
  deliveries: {
    resend: async (id: string): Promise<ClippingReportDeliverySummary> => {
      const response = await apiClient.post(`/clipping-reports/deliveries/${id}/resend`);
      return response.data;
    },
  },
  templates: {
    list: async (opts: { clientId?: string; inactive?: boolean } = {}): Promise<ClippingReportTemplate[]> => {
      const response = await apiClient.get("/clipping-reports/templates", {
        params: { clientId: opts.clientId || undefined, inactive: opts.inactive ? 1 : undefined },
      });
      return response.data;
    },
    catalog: async (): Promise<ClippingReportTemplateCatalog> => {
      const response = await apiClient.get("/clipping-reports/templates/catalog");
      return response.data;
    },
    get: async (id: string): Promise<ClippingReportTemplate> => {
      const response = await apiClient.get(`/clipping-reports/templates/${id}`);
      return response.data;
    },
    create: async (data: { clientId?: string | null; name: string; description?: string | null; definition: ClippingReportTemplateDefinition }): Promise<ClippingReportTemplate> => {
      const response = await apiClient.post("/clipping-reports/templates", data);
      return response.data;
    },
    update: async (id: string, data: Partial<{ name: string; description: string | null; definition: ClippingReportTemplateDefinition; isActive: boolean }>): Promise<ClippingReportTemplate> => {
      const response = await apiClient.patch(`/clipping-reports/templates/${id}`, data);
      return response.data;
    },
    duplicate: async (id: string, data: { name?: string; clientId?: string | null } = {}): Promise<ClippingReportTemplate> => {
      const response = await apiClient.post(`/clipping-reports/templates/${id}/duplicate`, data);
      return response.data;
    },
    remove: async (id: string) => {
      const response = await apiClient.delete(`/clipping-reports/templates/${id}`);
      return response.data;
    },
    restore: async (id: string): Promise<ClippingReportTemplate> => {
      const response = await apiClient.post(`/clipping-reports/templates/${id}/restore`);
      return response.data;
    },
    preview: async (id: string, data: { clientId: string; from: string; to: string; formats?: ClippingReportFormat[] }): Promise<ClippingReportRun> => {
      const response = await apiClient.post(`/clipping-reports/templates/${id}/preview`, data);
      return response.data;
    },
    files: async (id: string): Promise<ClippingReportTemplateFile[]> => {
      const response = await apiClient.get(`/clipping-reports/templates/${id}/files`);
      return response.data;
    },
    uploadFile: async (id: string, format: "PPTX" | "DOCX", file: File): Promise<ClippingReportTemplateFile> => {
      const form = new FormData();
      form.append("file", file);
      const response = await apiClient.post(`/clipping-reports/templates/${id}/files/${format}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      return response.data;
    },
    validateFile: async (id: string, format: "PPTX" | "DOCX", file: File): Promise<{ ok: boolean; errors: string[]; warnings: string[]; placeholders: { scalars: string[]; loops: string[]; images: string[] } }> => {
      const form = new FormData();
      form.append("file", file);
      const response = await apiClient.post(`/clipping-reports/templates/${id}/files/${format}/validate`, form, { headers: { "Content-Type": "multipart/form-data" } });
      return response.data;
    },
    removeFile: async (id: string, format: "PPTX" | "DOCX") => {
      const response = await apiClient.delete(`/clipping-reports/templates/${id}/files/${format}`);
      return response.data;
    },
    downloadFile: async (id: string, format: "PPTX" | "DOCX"): Promise<Blob> => {
      const response = await apiClient.get(`/clipping-reports/templates/${id}/files/${format}`, { responseType: "blob" });
      return response.data;
    },
  },
};

export const departmentsApi = {
  list: async (opts: { onlyInactive?: boolean } = {}): Promise<Department[]> => {
    const response = await apiClient.get("/departments", {
      params: opts.onlyInactive ? { onlyInactive: "1" } : undefined,
    });
    return response.data;
  },
  create: async (data: { slug: string; name: string; icon?: string | null }): Promise<Department> => {
    const response = await apiClient.post("/departments", data);
    return response.data;
  },
  update: async (id: string, data: { name?: string; icon?: string | null }): Promise<Department> => {
    const response = await apiClient.patch(`/departments/${id}`, data);
    return response.data;
  },
  /** Baja reversible. */
  remove: async (id: string) => {
    const response = await apiClient.delete(`/departments/${id}`);
    return response.data;
  },
  restore: async (id: string) => {
    const response = await apiClient.patch(`/departments/${id}/restore`);
    return response.data;
  },
  hardDelete: async (id: string) => {
    const response = await apiClient.delete(`/departments/${id}/permanent`);
    return response.data;
  },
};

export const rolesApi = {
  /** Returns effective permissions for the current user (union of all their roles) */
  getMyPermissions: async (): Promise<PermissionsMap> => {
    const response = await apiClient.get("/roles/permissions/me");
    return response.data;
  },

  /** Returns all roles with all their permissions — for management UI.
   *  `tenantId` is required when the caller is super-admin and wants to
   *  scope to a specific tenant. */
  getAllPermissions: async (
    tenantId?: string,
  ): Promise<Record<string, PermissionsMap>> => {
    const response = await apiClient.get("/roles/permissions", {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  /** Returns features with their labels and available actions */
  getFeatures: async (): Promise<FeatureMeta[]> => {
    const response = await apiClient.get("/roles/features");
    return response.data;
  },

  /** Update a specific role+feature permission in the targeted tenant. */
  updatePermission: async (
    role: string,
    feature: string,
    data: Partial<PermissionFlags>,
    tenantId?: string,
  ): Promise<void> => {
    await apiClient.put(`/roles/permissions/${role}/${feature}`, data, {
      params: tenantId ? { tenantId } : undefined,
    });
  },

  /** List all roles (built-in + custom) — super-admin only. */
  listRoles: async (
    tenantId?: string,
    opts: { includeInactive?: boolean; onlyInactive?: boolean } = {},
  ) => {
    const params: Record<string, string> = {};
    if (tenantId) params.tenantId = tenantId;
    if (opts.includeInactive) params.includeInactive = "true";
    if (opts.onlyInactive) params.onlyInactive = "true";
    const response = await apiClient.get("/roles", {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  /** List roles as options for user assignment — requires usuarios.canEdit */
  listRoleOptions: async () => {
    const response = await apiClient.get("/roles/options");
    return response.data;
  },

  /** Create a new custom role in the targeted tenant. */
  createRole: async (
    data: { name: string; label: string },
    tenantId?: string,
  ) => {
    const response = await apiClient.post("/roles", data, {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  /** Update a role's display label */
  updateRole: async (
    id: string,
    data: { label: string },
    tenantId?: string,
  ) => {
    const response = await apiClient.patch(`/roles/${id}`, data, {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  /** Soft-delete (dar de baja) un rol */
  deleteRole: async (id: string, tenantId?: string): Promise<void> => {
    await apiClient.delete(`/roles/${id}`, {
      params: tenantId ? { tenantId } : undefined,
    });
  },

  /** Restaura un rol soft-borrado */
  restoreRole: async (id: string, tenantId?: string): Promise<void> => {
    await apiClient.patch(`/roles/${id}/restore`, undefined, {
      params: tenantId ? { tenantId } : undefined,
    });
  },

  /** Borrado físico real de un rol previamente dado de baja */
  hardDeleteRole: async (id: string, tenantId?: string): Promise<void> => {
    await apiClient.delete(`/roles/${id}/permanent`, {
      params: tenantId ? { tenantId } : undefined,
    });
  },
};

// ============================================================================
// NOTIFICATIONS API
// ============================================================================

export const notificationsApi = {
  getAll: async (query?: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (query?.page) params.set("page", String(query.page));
    if (query?.limit) params.set("limit", String(query.limit));
    if (query?.unreadOnly) params.set("unreadOnly", "true");
    const qs = params.toString();
    const response = await apiClient.get(`/notifications${qs ? `?${qs}` : ""}`);
    return response.data;
  },

  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get("/notifications/unread-count");
    return response.data;
  },

  markAsRead: async (id: string) => {
    const response = await apiClient.patch(`/notifications/${id}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await apiClient.patch("/notifications/read-all");
    return response.data;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete(`/notifications/${id}`);
    return response.data;
  },

  deleteMany: async (ids: string[]) => {
    const response = await apiClient.delete("/notifications/bulk", {
      data: { ids },
    });
    return response.data;
  },

  getPreferences: async () => {
    const response = await apiClient.get("/notifications/preferences");
    return response.data;
  },

  updatePreferences: async (
    preferences: Array<{
      eventType: string;
      channel: string;
      enabled: boolean;
    }>,
  ) => {
    const response = await apiClient.put("/notifications/preferences", {
      preferences,
    });
    return response.data;
  },

  // Admin — notification types
  adminListTypes: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    category?: string;
    severity?: string;
  }) => {
    const response = await apiClient.get("/notifications/admin/types", {
      params,
    });
    return response.data;
  },

  adminCreateType: async (data: {
    eventKey: string;
    label: string;
    description?: string;
    severity?: string;
    category: string;
    icon?: string;
    color?: string;
  }) => {
    const response = await apiClient.post("/notifications/admin/types", data);
    return response.data;
  },

  adminUpdateType: async (
    id: string,
    data: {
      label?: string;
      description?: string;
      severity?: string;
      category?: string;
      icon?: string;
      color?: string;
      isActive?: boolean;
    },
  ) => {
    const response = await apiClient.patch(
      `/notifications/admin/types/${id}`,
      data,
    );
    return response.data;
  },

  adminDeleteType: async (id: string) => {
    const response = await apiClient.delete(`/notifications/admin/types/${id}`);
    return response.data;
  },

  // Admin — notification rules
  adminListRules: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    notificationTypeId?: string;
  }) => {
    const response = await apiClient.get("/notifications/admin/rules", {
      params,
    });
    return response.data;
  },

  adminCreateRule: async (data: {
    name: string;
    notificationTypeId: string;
    conditions?: unknown;
    titleTemplate: string;
    bodyTemplate?: string;
    severity?: string;
    recipientType: string;
    recipientConfig: Record<string, unknown>;
    excludeActor?: boolean;
    priority?: number;
  }) => {
    const response = await apiClient.post("/notifications/admin/rules", data);
    return response.data;
  },

  adminUpdateRule: async (
    id: string,
    data: {
      name?: string;
      conditions?: unknown;
      titleTemplate?: string;
      bodyTemplate?: string;
      severity?: string;
      recipientType?: string;
      recipientConfig?: Record<string, unknown>;
      excludeActor?: boolean;
      priority?: number;
      isActive?: boolean;
    },
  ) => {
    const response = await apiClient.patch(
      `/notifications/admin/rules/${id}`,
      data,
    );
    return response.data;
  },

  adminDeleteRule: async (id: string) => {
    const response = await apiClient.delete(`/notifications/admin/rules/${id}`);
    return response.data;
  },

  adminTestRule: async (id: string, payload: Record<string, unknown>) => {
    const response = await apiClient.post(
      `/notifications/admin/rules/${id}/test`,
      { payload },
    );
    return response.data;
  },

  adminSendTest: async (data: {
    ruleId?: string;
    title?: string;
    body?: string;
    severity?: string;
    channels?: string[];
  }) => {
    const response = await apiClient.post(
      "/notifications/admin/send-test",
      data,
    );
    return response.data;
  },
};

// ============================================================================
// MAINTENANCE & DATABASE API
// ============================================================================

export const maintenanceApi = {
  getLogHistory: async (params: {
    page?: number;
    limit?: number;
    level?: string;
    context?: string;
    search?: string;
    from?: string;
    to?: string;
  }): Promise<{
    data: {
      id: string;
      ts: string;
      level: string;
      context: string;
      message: string;
    }[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.level) qs.set("level", params.level);
    if (params.context) qs.set("context", params.context);
    if (params.search) qs.set("search", params.search);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    const response = await apiClient.get(`/maintenance/logs/history?${qs}`);
    return response.data;
  },
};

export const databaseApi = {
  listTables: async (): Promise<
    { table_name: string; row_count: string }[]
  > => {
    const response = await apiClient.get("/database/tables");
    return response.data;
  },

  getColumns: async (
    table: string,
  ): Promise<
    {
      column_name: string;
      data_type: string;
      is_nullable: string;
      column_default: string | null;
    }[]
  > => {
    const response = await apiClient.get(
      `/database/tables/columns?table=${table}`,
    );
    return response.data;
  },

  getTableData: async (
    table: string,
    page?: number,
    limit?: number,
  ): Promise<{ data: Record<string, unknown>[]; total: number }> => {
    const params = new URLSearchParams({ table });
    if (page) params.append("page", String(page));
    if (limit) params.append("limit", String(limit));
    const response = await apiClient.get(`/database/tables/data?${params}`);
    return response.data;
  },

  executeQuery: async (
    sql: string,
  ): Promise<{
    rows?: Record<string, unknown>[];
    rowCount?: number;
    duration?: number;
    type?: string;
    error?: string;
  }> => {
    const response = await apiClient.post("/database/query", { sql });
    return response.data;
  },
};

// ============================================================================
// USER SCHEDULED TASKS API (tareas configurables por usuario)
// ============================================================================

export const userScheduledTasksApi = {
  getAll: async (query?: {
    isActive?: boolean;
    actionType?: string;
    frequencyType?: string;
  }) => {
    const response = await apiClient.get("/scheduled-tasks/user", {
      params: query,
    });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await apiClient.get(`/scheduled-tasks/user/${id}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    actionType: string;
    actionConfig: Record<string, unknown>;
    frequencyType: "WEEKLY" | "MONTHLY";
    frequencyConfig: { dayOfWeek?: number; dayOfMonth?: number };
    executionTime: string;
  }) => {
    const response = await apiClient.post("/scheduled-tasks/user", data);
    return response.data;
  },

  update: async (id: string, data: Partial<Record<string, unknown>>) => {
    const response = await apiClient.patch(`/scheduled-tasks/user/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/scheduled-tasks/user/${id}`);
  },

  activate: async (id: string) => {
    const response = await apiClient.patch(
      `/scheduled-tasks/user/${id}/activate`,
    );
    return response.data;
  },

  deactivate: async (id: string) => {
    const response = await apiClient.patch(
      `/scheduled-tasks/user/${id}/deactivate`,
    );
    return response.data;
  },

  execute: async (id: string) => {
    const response = await apiClient.post(
      `/scheduled-tasks/user/${id}/execute`,
    );
    return response.data;
  },

  getExecutions: async (id: string) => {
    const response = await apiClient.get(
      `/scheduled-tasks/user/${id}/executions`,
    );
    return response.data;
  },
};

// ============================================================================
// AUDIT API — Timeline genérico de eventos por entidad
// ============================================================================

export type AuditAction =
  "CREATE" | "UPDATE" | "DELETE" | "RESTORE" | "STATUS_CHANGE" | "CUSTOM";

export interface AuditChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface AuditActor {
  id: string;
  name: string;
  email: string;
  avatar?: string | null;
}

export interface AuditEntry {
  id: string;
  action: AuditAction;
  createdAt: string;
  user: AuditActor | null;
  /** Quién actuaba de verdad si la acción se hizo suplantando a `user`. */
  impersonator?: {
    id: string;
    name: string | null;
    email: string | null;
    avatar?: string | null;
  } | null;
  changes: AuditChange[] | null;
  metadata: Record<string, unknown> | null;
  /**
   * Nombre de los ids que aparecen como valor en `changes`/`metadata`
   * (rol, departamento, usuario…), resuelto por el backend: `id → nombre`.
   */
  valueLabels?: Record<string, string> | null;
}

export interface AuditTimelineResponse {
  items: AuditEntry[];
  nextCursor: string | null;
}

export interface AuditListEntry extends AuditEntry {
  entityType: string;
  entityId: string;
  /** Nombre legible de la entidad ("Ana López (ana@…)"); null si no se resolvió. */
  entityLabel?: string | null;
}

/** Vía por la que se hizo una acción: persona, suplantación o proceso automático. */
export type AuditOrigin = "user" | "impersonation" | "automatic";

export interface AuditListResponse {
  items: AuditListEntry[];
  nextCursor: string | null;
}

export interface AuditListParams {
  limit?: number;
  cursor?: string;
  actions?: AuditAction[];
  entityType?: string;
  userId?: string;
  from?: string;
  to?: string;
  search?: string;
  /** Evento concreto (`metadata.event`), p. ej. LOGIN_FAILURE. Solo historial de sistema. */
  event?: string;
  /** Vía: persona, suplantación o proceso automático. Solo historial de sistema. */
  origin?: AuditOrigin;
}

export const auditApi = {
  getTimeline: async (
    entityType: string,
    entityId: string,
    params?: { limit?: number; cursor?: string; actions?: AuditAction[] },
  ): Promise<AuditTimelineResponse> => {
    const q: Record<string, string | number> = {};
    if (params?.limit) q.limit = params.limit;
    if (params?.cursor) q.cursor = params.cursor;
    if (params?.actions && params.actions.length > 0) {
      q.actions = params.actions.join(",");
    }
    const response = await apiClient.get(`/audit/${entityType}/${entityId}`, {
      params: q,
    });
    return response.data;
  },
  list: async (params?: AuditListParams): Promise<AuditListResponse> => {
    const q: Record<string, string | number> = {};
    if (params?.limit) q.limit = params.limit;
    if (params?.cursor) q.cursor = params.cursor;
    if (params?.actions && params.actions.length > 0) {
      q.actions = params.actions.join(",");
    }
    if (params?.entityType) q.entityType = params.entityType;
    if (params?.userId) q.userId = params.userId;
    if (params?.from) q.from = params.from;
    if (params?.to) q.to = params.to;
    if (params?.search) q.search = params.search;
    const response = await apiClient.get("/audit", { params: q });
    return response.data;
  },
  listSystem: async (params?: AuditListParams): Promise<AuditListResponse> => {
    const q: Record<string, string | number> = {};
    if (params?.limit) q.limit = params.limit;
    if (params?.cursor) q.cursor = params.cursor;
    if (params?.actions && params.actions.length > 0) {
      q.actions = params.actions.join(",");
    }
    if (params?.entityType) q.entityType = params.entityType;
    if (params?.userId) q.userId = params.userId;
    if (params?.from) q.from = params.from;
    if (params?.to) q.to = params.to;
    if (params?.search) q.search = params.search;
    if (params?.event) q.event = params.event;
    if (params?.origin) q.origin = params.origin;
    const response = await apiClient.get("/audit/system", { params: q });
    return response.data;
  },
  /** Personas con actividad en el historial de sistema (filtro "Usuario"). */
  getSystemActors: async (): Promise<Array<{ id: string; name: string; email: string }>> => {
    const response = await apiClient.get("/audit/system/actors");
    return response.data;
  },
  getEntityTypes: async (): Promise<{ entityTypes: string[] }> => {
    const response = await apiClient.get("/audit/entity-types");
    return response.data;
  },
  getSystemEntityTypes: async (): Promise<{ entityTypes: string[] }> => {
    const response = await apiClient.get("/audit/system/entity-types");
    return response.data;
  },
};

// ============================================================================
// SCHEDULED TASKS API (Sistema — tareas programadas)
// ============================================================================

export const scheduledTasksApi = {
  catalog: async (): Promise<import("@/types").ScheduledTaskCatalogItem[]> => {
    const r = await apiClient.get("/scheduled-tasks/catalog");
    return r.data;
  },
  getAll: async (params?: {
    isActive?: boolean;
    actionType?: string;
    isSystem?: boolean;
  }): Promise<import("@/types").ScheduledTask[]> => {
    const r = await apiClient.get("/scheduled-tasks", { params });
    return r.data;
  },
  getById: async (id: string): Promise<import("@/types").ScheduledTask> => {
    const r = await apiClient.get(`/scheduled-tasks/${id}`);
    return r.data;
  },
  create: async (
    data: Partial<import("@/types").ScheduledTask>,
  ): Promise<import("@/types").ScheduledTask> => {
    const r = await apiClient.post("/scheduled-tasks", data);
    return r.data;
  },
  update: async (
    id: string,
    data: Partial<import("@/types").ScheduledTask>,
  ): Promise<import("@/types").ScheduledTask> => {
    const r = await apiClient.patch(`/scheduled-tasks/${id}`, data);
    return r.data;
  },
  activate: async (id: string): Promise<import("@/types").ScheduledTask> => {
    const r = await apiClient.patch(`/scheduled-tasks/${id}/activate`);
    return r.data;
  },
  deactivate: async (id: string): Promise<import("@/types").ScheduledTask> => {
    const r = await apiClient.patch(`/scheduled-tasks/${id}/deactivate`);
    return r.data;
  },
  run: async (id: string): Promise<{ executionId: string; status: string }> => {
    const r = await apiClient.post(`/scheduled-tasks/${id}/run`);
    return r.data;
  },
  hardDelete: async (id: string): Promise<void> => {
    await apiClient.delete(`/scheduled-tasks/${id}`);
  },
  executions: async (
    id: string,
    params?: { page?: number; pageSize?: number },
  ): Promise<{
    data: import("@/types").ScheduledTaskExecution[];
    meta: { total: number; page: number; pageSize: number; totalPages: number };
  }> => {
    const r = await apiClient.get(`/scheduled-tasks/${id}/executions`, {
      params,
    });
    return r.data;
  },
};

// ============================================================================
// EMAIL OUTBOX API (Sistema → Correos, solo lectura)
// ============================================================================

export const emailOutboxApi = {
  getAll: async (
    query?: PaginationQuery,
  ): Promise<PaginatedResponse<import("@/types").EmailOutboxListItem>> => {
    const r = await apiClient.get(`/email-outbox${buildParams(query)}`);
    return r.data;
  },
  stats: async (
    query?: PaginationQuery,
  ): Promise<import("@/types").EmailOutboxStats> => {
    const r = await apiClient.get(`/email-outbox/stats${buildParams(query)}`);
    return r.data;
  },
  getById: async (id: string): Promise<import("@/types").EmailOutboxDetail> => {
    const r = await apiClient.get(`/email-outbox/${id}`);
    return r.data;
  },
};

// ─── Agentes: comunicación entre agentes de OpenClaw ─────────────────────────

export type AgentCommEventType =
  | "spawn"
  | "send"
  | "yield"
  | "settle"
  | "turn_start"
  | "turn_end"
  | "tool"
  | "inbound"
  | "outbound";

/** Prefijo de los ids de canal (`channel:telegram`) en `from`/`to`. */
export const CHANNEL_ID_PREFIX = "channel:";

export interface AgentCommEvent {
  id: string;
  ts: string;
  type: AgentCommEventType;
  from: string;
  to?: string;
  sessionId: string;
  sessionKey?: string;
  label?: string;
  text?: string;
  tool?: string;
  meta?: Record<string, unknown>;
}

export type AgentStatus = "working" | "waiting" | "idle" | "unknown";

export interface AgentNode {
  id: string;
  name: string;
  /** Icono de la librería de la app (nombre de lucide) o un emoji antiguo. */
  icon?: string | null;
  model?: string;
  parentId: string | null;
  level: number;
  configured: boolean;
  status: AgentStatus;
  lastActivityAt: string | null;
  openTurnSince: string | null;
  waitingSince: string | null;
  counters: {
    spawns: number;
    sends: number;
    settles: number;
    turns: number;
    tools: number;
  };
  tokens: { input: number; output: number };
}

export interface AgentEdge {
  from: string;
  to: string;
}

/** Prefijo de los ids de servicio externo (`integration:mail`) en `to`. */
export const INTEGRATION_ID_PREFIX = "integration:";

export interface ChannelNode {
  id: string;
  name: string;
  enabled: boolean;
  boundTo: string[];
}

export interface IntegrationNode {
  id: string;
  name: string;
  kind: "mcp" | "web";
  agents: string[];
}

export interface AgentCommsSource {
  available: boolean;
  agentsDir: string;
  configFile: string;
  files: number;
  lastScanAt: string | null;
  error?: string;
}

export interface AgentCommsSnapshot {
  generatedAt: string;
  source: AgentCommsSource;
  agents: AgentNode[];
  edges: AgentEdge[];
  channels: ChannelNode[];
  integrations: IntegrationNode[];
  limits: {
    maxSpawnDepth?: number;
    maxConcurrent?: number;
    maxChildrenPerAgent?: number;
    runTimeoutSeconds?: number;
  };
  events: AgentCommEvent[];
}

export type AgentCommsStreamMessage =
  | { type: "hello"; generatedAt: string }
  | { type: "event"; event: AgentCommEvent }
  | {
      type: "agents";
      agents: AgentNode[];
      source?: AgentCommsSource;
      generatedAt: string;
    }
  | { type: "source"; source: AgentCommsSource }
  | { type: "usage"; record: UsageRecord };

// ─── Uso de tokens y gasto de los agentes (dashboard) ─────────────────────────

export type UsageRange = "today" | "7d" | "30d";

/** USD por millón de tokens. */
export interface ModelPricing {
  input: number;
  cacheRead: number;
  cacheWrite: number;
  output: number;
}

export interface UsageTokens {
  input: number;
  cached: number;
  cacheWrite: number;
  output: number;
  reasoning: number;
}

export interface UsageRecord extends UsageTokens {
  id: string;
  ts: string;
  agentId: string;
  sessionId: string;
  model?: string;
  /** USD; `null` si el modelo no tiene tarifa conocida. */
  cost: number | null;
}

export interface UsageTotals extends UsageTokens {
  turns: number;
  cost: number;
  unpriced: number;
}

export interface UsageBucket {
  start: string;
  cost: number;
  tokens: number;
  turns: number;
  costByAgent: Record<string, number>;
  tokensByAgent: Record<string, number>;
}

export interface UsageByAgent extends UsageTotals {
  agentId: string;
  name: string;
  model?: string;
  status: string;
  lastActivityAt: string | null;
  share: number;
  avgCostPerTurn: number;
}

export interface UsageByModel extends UsageTotals {
  model: string;
  agents: string[];
  pricing: ModelPricing | null;
}

export interface AgentUsageSummary {
  generatedAt: string;
  range: UsageRange;
  from: string;
  to: string;
  tzOffsetMinutes: number;
  currency: "USD";
  totals: UsageTotals;
  previous: UsageTotals;
  series: UsageBucket[];
  byAgent: UsageByAgent[];
  byModel: UsageByModel[];
  recent: UsageRecord[];
  live: {
    working: number;
    waiting: number;
    lastHour: UsageTotals;
    lastRecordAt: string | null;
  };
  loaded: UsageTotals & { since: string | null };
  pricing: Record<string, ModelPricing>;
  /** Tipo de cambio USD→EUR (BCE, variable de entorno o valor fijo). */
  fx?: { eurPerUsd: number; asOf: string; source: "ecb" | "env" | "default" };
}

export type UsageCurrency = "USD" | "EUR";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:4000/api";

export const agentCommsApi = {
  /** Estado inicial: organigrama, estado de cada agente y últimos eventos. */
  snapshot: async (limit = 500): Promise<AgentCommsSnapshot> => {
    const response = await apiClient.get("/agent-comms/snapshot", {
      params: { limit },
    });
    return response.data;
  },
  /** URL del SSE (`EventSource` no manda cabeceras: el token va por query,
   *  que la estrategia JWT sólo acepta bajo `/realtime/`). */
  streamUrl: (token: string): string =>
    `${API_URL}/realtime/agent-comms/stream?token=${encodeURIComponent(token)}`,
  /** Uso de tokens y gasto estimado. `tz`: minutos al este de UTC del navegador. */
  usage: async (range: UsageRange, tz: number): Promise<AgentUsageSummary> => {
    const response = await apiClient.get("/agent-comms/usage", {
      params: { range, tz },
    });
    return response.data;
  },
};

// ─── Conocimiento: mapa del corpus vectorial ─────────────────────────────────

export interface KnowledgeBrand {
  slug: string;
  name: string;
  legal?: string;
  articles: number;
}

export interface KnowledgeAuthor {
  slug: string;
  name: string;
  type?: string;
  articles: number;
}

export interface KnowledgeArticle {
  id: number;
  title: string;
  brand: string;
  author: string;
  format: string;
  language: string | null;
  publishedOn: string | null;
  wordCount: number;
  fragments: number;
  source: string;
  reserved: boolean;
  excerpt: string;
  keywords: string[];
  x: number;
  y: number;
  cluster: number;
}

export interface KnowledgeLink {
  a: number;
  b: number;
  similarity: number;
}

export interface KnowledgeCluster {
  id: number;
  brand: string;
  label: string[];
  size: number;
  x: number;
  y: number;
}

export interface KnowledgeMap {
  generatedAt: string;
  source: {
    available: boolean;
    error?: string;
    semanticSearch: boolean;
    canIngest: boolean;
    canDelete: boolean;
  };
  embedding: { dimensions: number | null; fragments: number };
  brands: KnowledgeBrand[];
  authors: KnowledgeAuthor[];
  formats: string[];
  languages: string[];
  articles: KnowledgeArticle[];
  links: KnowledgeLink[];
  clusters: KnowledgeCluster[];
}

export interface KnowledgeArticleDetail {
  id: number;
  title: string;
  brand: string;
  author: string;
  format: string;
  language: string | null;
  publishedOn: string | null;
  wordCount: number;
  source: string;
  reserved: boolean;
  content: string;
}

export interface KnowledgeSearchHit {
  id: number;
  title: string;
  similarity: number;
  text: string;
}

// ============================================================================
// AGENTS ADMIN API (gestión de agentes)
// ============================================================================
export type AgentCapability = "internet" | "corpus" | "mail" | "fleet";

export interface AgentConfigDetails {
  identityName: string | null;
  emoji: string | null;
  skills: string[];
  toolsDeny: string[];
  allowAgents: string[];
  workspace: string | null;
  capabilities: Record<AgentCapability, boolean>;
  /** Agentes que pueden delegar en él. */
  delegableBy: string[];
}

export interface AgentsMeta {
  manageable: boolean;
  models: Array<{ id: string; name: string; provider: string }>;
  skills: string[];
  agents: Array<{ id: string; name: string }>;
}

export interface AgentInput {
  identityName?: string;
  emoji?: string | null;
  model?: string;
  skills?: string[];
  allowAgents?: string[];
  delegableBy?: string[];
  capabilities?: Partial<Record<AgentCapability, boolean>>;
}

export interface AgentListItem {
  id: string;
  name: string;
  model?: string;
  parentId: string | null;
  level: number;
  configured: boolean;
  status: "working" | "waiting" | "idle" | "unknown";
  lastActivityAt: string | null;
  counters: { spawns: number; sends: number; settles: number; turns: number; tools: number };
  tokens: { input: number; output: number };
  cost: number;
  /** Agente de origen (su programa lo monta el compose; no se borra desde la app). */
  core: boolean;
  /** `false`: nadie puede delegar en él hasta reactivarlo. */
  active: boolean;
  config: AgentConfigDetails;
  program: { file: string; exists: boolean; updatedAt: string | null; bytes: number; editable: boolean };
}

export interface AgentDetail extends AgentListItem {
  programContent: string | null;
}

export const agentsAdminApi = {
  list: async (): Promise<AgentListItem[]> => {
    const response = await apiClient.get("/agents");
    return response.data;
  },
  meta: async (): Promise<AgentsMeta> => {
    const response = await apiClient.get("/agents/meta");
    return response.data;
  },
  get: async (id: string): Promise<AgentDetail> => {
    const response = await apiClient.get(`/agents/${id}`);
    return response.data;
  },
  create: async (
    data: AgentInput & { id: string; identityName: string; model: string; program: string },
  ): Promise<{ id: string }> => {
    const response = await apiClient.post("/agents", data);
    return response.data;
  },
  update: async (id: string, data: AgentInput): Promise<{ id: string }> => {
    const response = await apiClient.patch(`/agents/${id}`, data);
    return response.data;
  },
  remove: async (id: string): Promise<{ id: string }> => {
    const response = await apiClient.delete(`/agents/${id}`);
    return response.data;
  },
  /** Activa o desactiva un agente (inactivo = nadie delega en él). */
  setActive: async (id: string, active: boolean): Promise<{ id: string; active: boolean }> => {
    const response = await apiClient.patch(`/agents/${id}/active`, { active });
    return response.data;
  },
  saveProgram: async (
    id: string,
    content: string,
  ): Promise<AgentListItem["program"]> => {
    const response = await apiClient.put(`/agents/${id}/program`, { content });
    return response.data;
  },
};

// ============================================================================
// PRESS RELEASES API (Mis notas de prensa)
// ============================================================================
export type PressReleaseStatus = "DRAFT" | "APPROVED" | "REJECTED";

export interface PressReleaseListItem {
  id: string;
  title: string;
  channel: string;
  senderName: string | null;
  senderUsername: string | null;
  request: string | null;
  delegated: string[];
  /** Nombres legibles de `delegated`, en el mismo orden. */
  delegatedNames: string[];
  deliveredAt: string;
  userId: string | null;
  /** Dada de baja (se puede restaurar o eliminar definitivamente). */
  hiddenAt: string | null;
  status: PressReleaseStatus;
  decidedAt: string | null;
  decisionNote: string | null;
  decidedBy: { id: string; name: string } | null;
}

export interface PressRelease extends PressReleaseListItem {
  body: string;
  chatId: string;
  senderId: string | null;
  sessionId: string;
  sessionKey: string | null;
  agentId: string;
}

export interface PressReleaseList {
  data: PressReleaseListItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
  /** Identidades enlazadas del usuario: sin ninguna no puede haber notas propias. */
  linked: { telegram: boolean; slack: boolean; whatsapp: boolean };
}

export type LinkableChannel = "telegram" | "whatsapp";

export interface ChannelIdentity {
  id: string;
  channel: string;
  senderId: string;
  senderName: string | null;
  senderUsername: string | null;
  linkedVia: string;
  linkedAt: string;
}

export interface PendingLink {
  /** `VINCULAR-XXXXXX`, por si hay que escribirlo a mano. */
  code: string;
  channel: string;
  expiresAt: string;
  /** Abre el bot con el código ya puesto; `null` si el canal no está configurado. */
  deepLink: string | null;
  /** El enlace como QR (SVG en data URI). */
  qrSvg: string | null;
}

export interface ChannelLinkStatus {
  identities: ChannelIdentity[];
  /** Código vigente sin usar. */
  pending: PendingLink | null;
  /** Identidades de respaldo (columnas del usuario). */
  fallback: { telegram: boolean; slack: boolean };
  /** Canales con bot configurado para el enlace directo. */
  channels: { telegram: boolean; whatsapp: boolean };
}

/** Vinculación de la propia cuenta con Telegram/WhatsApp por código. */
export const channelLinkApi = {
  status: async (): Promise<ChannelLinkStatus> => {
    const response = await apiClient.get("/press-releases/link");
    return response.data;
  },
  create: async (channel: LinkableChannel): Promise<PendingLink> => {
    const response = await apiClient.post("/press-releases/link", { channel });
    return response.data;
  },
  /** Cancela el código pendiente. */
  cancel: async (): Promise<{ cancelled: number }> => {
    const response = await apiClient.delete("/press-releases/link/code");
    return response.data;
  },
  unlink: async (id: string): Promise<{ id: string }> => {
    const response = await apiClient.delete(`/press-releases/link/${id}`);
    return response.data;
  },
};

// ============================================================================
// CLIPPING (Editorial) — apariciones en medios asociadas a las notas de prensa
// ============================================================================

export type ClippingMediaType = "PRINT" | "DIGITAL" | "RADIO" | "TV" | "SOCIAL" | "OTHER";
export type ClippingSource = "MANUAL" | "IMPORT" | "HALLON" | "SIMBIU" | "MELTWATER";

export interface Clipping {
  id: string;
  brand: string;
  outlet: string;
  mediaType: ClippingMediaType;
  publishedAt: string;
  title: string;
  url: string | null;
  excerpt: string | null;
  audience: number | null;
  /** Valor publicitario equivalente, en euros. */
  adValue: number | null;
  source: ClippingSource;
  sourceRef: string | null;
  notes: string | null;
  // Lo que añaden los proveedores (Simbiu, Hallon); editable para completarlo.
  topic: string | null;
  section: string | null;
  author: string | null;
  page: number | null;
  /** 1 muy negativo … 5 muy positivo; null sin valoración. */
  sentiment: number | null;
  region: string | null;
  country: string | null;
  category: string | null;
  relevance: number | null;
  /** Valor de comunicación (Hallon), en euros. */
  commValue: number | null;
  keywords: string | null;
  thumbnailUrl: string | null;
  documentUrl: string | null;
  /** Radio y TV: clip de audio o vídeo en el proveedor (se reproduce por `clippingApi.media`), hora de emisión y duración. */
  mediaUrl: string | null;
  airTime: string | null;
  durationSeconds: number | null;
  externalId: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Una marca con clipping y sus cifras (portada de Clipping). */
export interface ClippingBrandSummary {
  brand: string;
  /** Logo resuelto solo (Wikidata o icono del sitio), servido en /uploads/brand-logos. */
  logoUrl: string | null;
  /** Máscara de tinta del logo (`<slug>.mask.png`): el componente la pinta en negro o blanco según el tema. */
  logoMaskUrl: string | null;
  /** Aún no se ha intentado resolver: conviene volver a consultar en unos segundos. */
  logoPending: boolean;
  count: number;
  last30Days: number;
  lastAt: string | null;
  outlets: number;
  audience: number;
  adValue: number;
  sources: ClippingSource[];
}

export interface ClippingList {
  data: Clipping[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface ClippingOptions {
  brands: string[];
  outlets: string[];
  mediaTypes: ClippingMediaType[];
  sources: ClippingSource[];
  /** Años con clipping, del más reciente al más antiguo. */
  years: number[];
}

export interface ClippingInput {
  brand: string;
  outlet: string;
  mediaType: ClippingMediaType;
  /** `YYYY-MM-DD`. */
  publishedAt: string;
  title: string;
  url?: string | null;
  excerpt?: string | null;
  audience?: number | null;
  adValue?: number | null;
  notes?: string | null;
  topic?: string | null;
  section?: string | null;
  author?: string | null;
  page?: number | null;
  sentiment?: number | null;
  region?: string | null;
  country?: string | null;
  category?: string | null;
  keywords?: string | null;
}

/** Estado de los proveedores de seguimiento conectados por API. */
/** Uso de la API del proveedor hoy: peticiones, presupuesto y pausa si la hay. */
export interface ClippingProviderUsage {
  day: string;
  requests: number;
  budget: number;
  pausedUntil: string | null;
  pauseReason: string | null;
  /** Otros topes del proveedor (búsquedas del día, exportaciones y documentos del mes…). */
  details?: Array<{ label: string; used: number; limit: number; period: "day" | "month" }>;
}

export type ClippingSyncProvider = "simbiu" | "meltwater";

/** Una cuenta (marca) de un proveedor, colgando de una de sus conexiones. */
export interface ClippingProviderAccount {
  id: number | string;
  name: string;
  brand: string;
  connection: string;
}

/** Una conexión (credenciales de un cliente) de un proveedor y lo que ve. */
export interface ClippingProviderConnection {
  id: string;
  label: string;
  accounts: ClippingProviderAccount[];
  usage: ClippingProviderUsage;
  error?: string;
}

export interface ClippingProviderStatus {
  key: ClippingSyncProvider;
  label: string;
  configured: boolean;
  connections: ClippingProviderConnection[];
  accounts: ClippingProviderAccount[];
  usage: ClippingProviderUsage;
  error?: string;
}

/** Estado de todos los proveedores, por clave (`GET /clipping/providers`). */
export type ClippingProviders = Partial<Record<ClippingSyncProvider, ClippingProviderStatus>>;

export interface ClippingSyncBrand {
  corporateId: number | string;
  corporate: string;
  connection: string;
  brand: string;
  fetched: number;
  created: number;
  skipped: number;
  errors: number;
  /** Nuevas que quedaron para la siguiente pasada (tope por marca). */
  pending: number;
  /** Quedan tramos del periodo sin revisar: repetir la sincronización con el mismo periodo. */
  truncated?: boolean;
  error?: string;
  paused?: boolean;
}

export interface ClippingSyncSummary {
  provider: ClippingSyncProvider;
  from: string;
  to: string;
  brands: ClippingSyncBrand[];
  fetched: number;
  created: number;
  skipped: number;
  errors: number;
  pending: number;
  /** Marcas a las que se les acabó la cuota del día antes de revisar todo el periodo. */
  truncated: number;
  /** Noticias de radio y TV ya importadas a las que se añadió el clip en esta pasada. */
  enriched: number;
  /** Peticiones a la API en esta pasada y uso del día. */
  requests: number;
  usage: ClippingProviderUsage;
  /** La pasada se cortó (pausa o presupuesto agotado). */
  aborted?: string;
}

/** Progreso de la sincronización en marcha (o resultado de la última). */
export interface ClippingSyncProgress {
  provider: ClippingSyncProvider | null;
  running: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  from: string | null;
  to: string | null;
  brandsTotal: number;
  brandsDone: number;
  currentBrand: string | null;
  /** Tramos de hasta un mes del periodo y por cuál va la marca en curso. */
  windowsTotal: number;
  windowsDone: number;
  currentWindow: string | null;
  itemsTotal: number;
  itemsDone: number;
  created: number;
  skipped: number;
  errors: number;
  percent: number;
  summary: ClippingSyncSummary | null;
  error: string | null;
}

// ─── Informe de clipping ───────────────────────────────────────────────────
export type ClippingReportMetric = "count" | "audience" | "adValue";

export interface ClippingReportParams {
  brand?: string;
  from: string;
  to: string;
  mediaTypes?: ClippingMediaType[];
  metric?: ClippingReportMetric;
}

export interface ClippingReportBucket {
  key: string;
  label: string;
  count: number;
  audience: number;
  adValue: number;
}

export interface ClippingReportData {
  brand: string | null;
  from: string;
  to: string;
  generatedAt: string;
  filters: { mediaTypes: ClippingMediaType[] };
  totals: {
    count: number;
    audience: number;
    adValue: number;
    commValue: number;
    outlets: number;
    withAudience: number;
    withAdValue: number;
  };
  byMediaType: ClippingReportBucket[];
  byTopic: ClippingReportBucket[];
  byOutlet: ClippingReportBucket[];
  byDay: Array<{ day: string; count: number; audience: number; adValue: number }>;
  /** Meses del periodo con su desglose por soporte (informes anuales y Excel de MG/Suzuki). */
  byMonth: Array<{
    month: string;
    count: number;
    audience: number;
    adValue: number;
    byMediaType: Partial<Record<ClippingMediaType, { count: number; audience: number; adValue: number }>>;
  }>;
  bySentiment: Array<{ sentiment: number; count: number }>;
  // ── Cortes del generador de informes (desde el 2026-11-03) ──
  byOutletAll?: ClippingReportBucket[];
  byAuthor?: ClippingReportBucket[];
  byCategory?: ClippingReportBucket[];
  bySection?: ClippingReportBucket[];
  byRegion?: ClippingReportBucket[];
  byCountry?: ClippingReportBucket[];
  byWeekday?: ClippingReportBucket[];
  byBrand?: ClippingReportBucket[];
  byEvent?: ClippingReportBucket[];
  sentimentByMediaType?: Array<{ key: string; label: string; count: number; positive: number; neutral: number; negative: number; unrated: number }>;
  /** El periodo anterior de la misma duración, para comparar. */
  previous: { from: string; to: string; totals: { count: number; audience: number; adValue: number }; byMediaType?: ClippingReportBucket[] };
  items: Array<{
    id: string;
    publishedAt: string;
    outlet: string;
    mediaType: ClippingMediaType;
    title: string;
    url: string | null;
    documentUrl: string | null;
    /** Enlace público firmado al documento (el de los informes entregados). */
    documentLink: string | null;
    audience: number | null;
    adValue: number | null;
    topic: string | null;
    sentiment: number | null;
    // Campos adicionales que trae el informe (vista previa de destacados, dossier y agrupados).
    excerpt?: string | null;
    author?: string | null;
    section?: string | null;
    category?: string | null;
    region?: string | null;
    country?: string | null;
    keywords?: string | null;
    relevance?: number | null;
    thumbnailUrl?: string | null;
  }>;
}

export interface ClippingImportSummary {
  file: string;
  rows: number;
  created: number;
  skipped: number;
  errors: Array<{ row: number; reason: string }>;
  mapping: Record<string, string>;
}

/** Query del informe: los soportes van como lista separada por comas. */
function reportQuery(p: ClippingReportParams) {
  return {
    brand: p.brand || undefined,
    from: p.from,
    to: p.to,
    mediaTypes: p.mediaTypes?.length ? p.mediaTypes.join(",") : undefined,
    metric: p.metric,
  };
}

export const clippingApi = {
  list: async (q: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    brand?: string;
    outlet?: string;
    title?: string;
    mediaType?: ClippingMediaType;
    source?: ClippingSource;
    from?: string;
    to?: string;
    inactive?: boolean;
  }): Promise<ClippingList> => {
    const response = await apiClient.get("/clipping", {
      params: {
        page: q.page,
        limit: q.limit,
        search: q.search || undefined,
        sortBy: q.sortBy,
        sortOrder: q.sortOrder,
        brand: q.brand || undefined,
        outlet: q.outlet || undefined,
        title: q.title || undefined,
        mediaType: q.mediaType || undefined,
        source: q.source || undefined,
        from: q.from || undefined,
        to: q.to || undefined,
        inactive: q.inactive ? 1 : undefined,
      },
    });
    return response.data;
  },
  options: async (): Promise<ClippingOptions> => {
    const response = await apiClient.get("/clipping/options");
    return response.data;
  },
  /** Marcas con clipping y sus cifras. */
  brands: async (): Promise<ClippingBrandSummary[]> => {
    const response = await apiClient.get("/clipping/brands");
    return response.data;
  },
  get: async (id: string): Promise<Clipping> => {
    const response = await apiClient.get(`/clipping/${id}`);
    return response.data;
  },
  create: async (data: ClippingInput): Promise<Clipping> => {
    const response = await apiClient.post("/clipping", data);
    return response.data;
  },
  update: async (id: string, data: Partial<ClippingInput>): Promise<Clipping> => {
    const response = await apiClient.patch(`/clipping/${id}`, data);
    return response.data;
  },
  /** Informe agregado de un periodo (y marca, soportes, nota) para la pantalla. */
  report: async (params: ClippingReportParams): Promise<ClippingReportData> => {
    const response = await apiClient.get("/clipping/report", { params: reportQuery(params) });
    return response.data;
  },
  /** El informe en PDF o Excel, como fichero. */
  reportFile: async (format: "pdf" | "xlsx", params: ClippingReportParams): Promise<Blob> => {
    const response = await apiClient.get(`/clipping/report/${format}`, {
      params: reportQuery(params),
      responseType: "blob",
      timeout: 3 * 60_000,
    });
    return response.data as Blob;
  },
  /** Clip de audio o vídeo (radio y TV), en streaming por el backend desde el proveedor. */
  media: async (id: string): Promise<Blob> => {
    const response = await apiClient.get(`/clipping/${id}/media`, { responseType: "blob", timeout: 5 * 60_000 });
    return response.data as Blob;
  },
  /** Documento completo (PDF) del clipping, servido por el backend con la sesión del proveedor. */
  document: async (id: string): Promise<Blob> => {
    const response = await apiClient.get(`/clipping/${id}/document`, { responseType: "blob", timeout: 2 * 60_000 });
    return response.data as Blob;
  },
  /** Proveedores de clipping por API: conexiones, cuentas (marcas) y uso de cada uno. */
  providers: async (): Promise<ClippingProviders> => {
    const response = await apiClient.get("/clipping/providers");
    return response.data;
  },
  /** Arranca en segundo plano la sincronización con un proveedor (una pasada a la vez); el progreso, con `syncStatus`. */
  sync: async (
    provider: ClippingSyncProvider,
    opts: { days?: number; from?: string; to?: string; brands?: string[]; connections?: string[] },
  ): Promise<{ started: boolean; status: ClippingSyncProgress }> => {
    const response = await apiClient.post(`/clipping/sync/${provider}`, opts);
    return response.data;
  },
  syncStatus: async (): Promise<ClippingSyncProgress> => {
    const response = await apiClient.get("/clipping/sync/status");
    return response.data;
  },
  /** Importa un Excel o CSV de un proveedor (puede tardar: vectoriza en segundo plano). */
  import: async (file: File, opts: { brand?: string; source?: ClippingSource }): Promise<ClippingImportSummary> => {
    const form = new FormData();
    form.append("file", file);
    if (opts.brand) form.append("brand", opts.brand);
    if (opts.source) form.append("source", opts.source);
    const response = await apiClient.post("/clipping/import", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 5 * 60_000,
    });
    return response.data;
  },
  remove: async (id: string): Promise<{ id: string }> => {
    const response = await apiClient.delete(`/clipping/${id}`);
    return response.data;
  },
  restore: async (id: string): Promise<{ id: string }> => {
    const response = await apiClient.patch(`/clipping/${id}/restore`);
    return response.data;
  },
  hardDelete: async (id: string): Promise<{ id: string }> => {
    const response = await apiClient.delete(`/clipping/${id}/permanent`);
    return response.data;
  },
};

export interface PressReleasesDashboard {
  scope: "all" | "mine";
  total: number;
  draft: number;
  approved: number;
  rejected: number;
  last30Days: number;
  oldestPendingAt: string | null;
  byChannel: Array<{ channel: string; count: number }>;
  recent: Array<{ id: string; title: string; status: string; channel: string; senderName: string | null; deliveredAt: string }>;
}

export const pressReleasesApi = {
  /** Dashboard de PR & Eventos: números y últimas notas (las propias, o todas si puede verlas). */
  dashboard: async (): Promise<PressReleasesDashboard> => {
    const response = await apiClient.get("/press-releases/dashboard");
    return response.data;
  },
  list: async (q: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    channel?: string;
    title?: string;
    senderName?: string;
    from?: string;
    to?: string;
    status?: PressReleaseStatus;
    inactive?: boolean;
    all?: boolean;
  }): Promise<PressReleaseList> => {
    const response = await apiClient.get("/press-releases", {
      params: {
        page: q.page,
        limit: q.limit,
        search: q.search || undefined,
        sortBy: q.sortBy,
        sortOrder: q.sortOrder,
        channel: q.channel || undefined,
        title: q.title || undefined,
        senderName: q.senderName || undefined,
        from: q.from || undefined,
        to: q.to || undefined,
        status: q.status || undefined,
        inactive: q.inactive ? 1 : undefined,
        all: q.all ? 1 : undefined,
      },
    });
    return response.data;
  },
  get: async (id: string): Promise<PressRelease> => {
    const response = await apiClient.get(`/press-releases/${id}`);
    return response.data;
  },
  /** Aprueba, rechaza o devuelve a borrador. */
  decide: async (
    id: string,
    status: PressReleaseStatus,
    note?: string,
  ): Promise<{
    id: string;
    title: string;
    status: PressReleaseStatus;
    previous: PressReleaseStatus;
  }> => {
    const response = await apiClient.patch(`/press-releases/${id}/status`, {
      status,
      note: note || undefined,
    });
    return response.data;
  },
  /** Da de baja la nota (no se borra: se puede restaurar). */
  hide: async (id: string): Promise<{ id: string; title: string }> => {
    const response = await apiClient.delete(`/press-releases/${id}`);
    return response.data;
  },
  restore: async (id: string): Promise<{ id: string; title: string }> => {
    const response = await apiClient.patch(`/press-releases/${id}/restore`);
    return response.data;
  },
  /** Eliminación definitiva; sólo desde una nota dada de baja. */
  hardDelete: async (id: string): Promise<{ id: string; title: string }> => {
    const response = await apiClient.delete(`/press-releases/${id}/permanent`);
    return response.data;
  },
};

export interface KnowledgeDeleteResult {
  id: number;
  title: string | null;
  brand: string;
  author: string;
  source: string;
  fragments: number;
  /** El fichero fuente también se ha retirado del corpus. */
  fileDeleted: boolean;
}

export const knowledgeApi = {
  map: async (refresh = false): Promise<KnowledgeMap> => {
    const response = await apiClient.get("/knowledge/map", {
      params: refresh ? { refresh: 1 } : undefined,
    });
    return response.data;
  },
  article: async (id: number): Promise<KnowledgeArticleDetail> => {
    const response = await apiClient.get(`/knowledge/articles/${id}`);
    return response.data;
  },
  search: async (
    query: string,
    limit = 10,
  ): Promise<{ query: string; hits: KnowledgeSearchHit[] }> => {
    const response = await apiClient.post("/knowledge/search", {
      query,
      limit,
    });
    return response.data;
  },
  /** Da de baja un artículo (fila, fragmentos y fichero fuente). */
  deleteArticle: async (id: number): Promise<KnowledgeDeleteResult> => {
    const response = await apiClient.delete(`/knowledge/articles/${id}`);
    return response.data;
  },
};

export interface KnowledgeIngestFileResult {
  file: string;
  status: "added" | "skipped" | "failed";
  reason?: string;
  id?: number;
  words?: number;
  fragments?: number;
}

export interface AddDocumentsResult {
  brand: string;
  author: string;
  saved: string[];
  unchanged: string[];
  catalogUpdated: { brand: boolean; author: boolean };
  ingest: {
    added: number;
    skipped: number;
    failed: number;
    fragments: number;
    files: KnowledgeIngestFileResult[];
    log: string[];
  };
}

export interface AddDocumentsInput {
  brand: string;
  author: string;
  format: string;
  reserved: boolean;
  brandName?: string;
  brandLegal?: string;
  authorName?: string;
  authorType?: "person" | "source";
  files: File[];
}

/** Sube documentos al corpus y lanza la ingesta (multipart). */
export async function addKnowledgeDocuments(
  input: AddDocumentsInput,
): Promise<AddDocumentsResult> {
  const form = new FormData();
  form.append("brand", input.brand);
  form.append("author", input.author);
  form.append("format", input.format);
  form.append("reserved", input.reserved ? "true" : "false");
  if (input.brandName) form.append("brandName", input.brandName);
  if (input.brandLegal) form.append("brandLegal", input.brandLegal);
  if (input.authorName) form.append("authorName", input.authorName);
  if (input.authorType) form.append("authorType", input.authorType);
  for (const f of input.files) form.append("files", f, f.name);
  const response = await apiClient.post("/knowledge/documents", form, {
    headers: { "Content-Type": "multipart/form-data" },
    // La ingesta vectoriza: puede tardar minutos con varios PDF.
    timeout: 10 * 60_000,
  });
  return response.data;
}

export interface AnalyzedDocument {
  token: string;
  file: string;
  size: number;
  words: number;
  title: string;
  publishedOn: string | null;
  brand: {
    slug: string | null;
    name: string;
    legal: string | null;
    isNew: boolean;
  };
  author: {
    slug: string | null;
    name: string;
    type: "person" | "source";
    isNew: boolean;
  };
  format: string;
  language: string;
  summary: string;
  confidence: number;
  nearest?: {
    id: number;
    title: string;
    brand: string;
    language?: string | null;
    similarity: number;
  } | null;
  warnings: Array<{
    code:
      | "brand-merged"
      | "brand-existing"
      | "author-existing"
      | "duplicate"
      | "translation";
    detail: string;
  }>;
  error?: string;
}

export interface CommitItem {
  token: string;
  brand: string;
  author: string;
  format: string;
  reserved: boolean;
  /** Fecha de publicación corregida a mano (AAAA-MM-DD). */
  publishedOn?: string;
  brandName?: string;
  brandLegal?: string;
  authorName?: string;
  authorType?: "person" | "source";
}

export interface CommitResult {
  saved: string[];
  unchanged: string[];
  catalogUpdated: { brands: string[]; authors: string[] };
  ingest: AddDocumentsResult["ingest"];
}

/** Paso 1: sube los ficheros y recibe la clasificación propuesta. */
export async function analyzeKnowledgeDocuments(
  files: File[],
): Promise<{ documents: AnalyzedDocument[] }> {
  const form = new FormData();
  for (const f of files) form.append("files", f, f.name);
  const response = await apiClient.post("/knowledge/documents/analyze", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 5 * 60_000,
  });
  return response.data;
}

/** Paso 2: confirma la clasificación y lanza la ingesta. */
export async function commitKnowledgeDocuments(
  items: CommitItem[],
): Promise<CommitResult> {
  const response = await apiClient.post(
    "/knowledge/documents/commit",
    { items },
    { timeout: 10 * 60_000 },
  );
  return response.data;
}
